import { Fragment, useState } from 'react';
import { useGetPtoVolumesSummaryQuery, type PtoMaterialRow } from '@shared/api';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import { fmtQty } from './format';
import { QtyNote } from './QtyCell';
import { Muted } from './styles';
import { shareSum } from './systems';
import { Facts, Grid, LinkButton, ShareTr, Toggle, ViewBar, WARN_SIGN, Warn } from './systemsUi';

interface Props {
  revisionId: string;
  /** Только строки «на проверку». */
  onlyReview: boolean;
}

const COLS = 11;

/** Вид «Сводно ▶»: ведомость материалов — тот же отбор, что у Excel (материал =
 *  наименование + марка + единица + масса; «Поз.» в ключ не входит). ▶
 *  раскрывает доли систем строки: сумма долей равна количеству строки, метка
 *  нескольких систем — одна доля, «не определена» и сомнение видны. В комплекте
 *  без систем ▶ раскрывает доли групп таблиц (замечание 23.10, п. 3: бетон и
 *  арматура — по каждой свае). */
export function SummaryTable({ revisionId, onlyReview }: Props) {
  const { data, isLoading, isError } = useGetPtoVolumesSummaryQuery(revisionId, {
    skip: !revisionId,
  });
  const [open, setOpen] = useState<Set<number>>(new Set());

  if (isLoading) return <Muted>Загрузка ведомости…</Muted>;
  if (isError || !data) return <Muted>Не удалось загрузить сводный вид ведомости.</Muted>;

  const rows = onlyReview ? data.rows.filter((r) => r.needs_review) : data.rows;
  const withShares = rows.filter((r) => shareRows(r).length > 0);

  function toggle(position: number) {
    const next = new Set(open);
    if (next.has(position)) next.delete(position);
    else next.add(position);
    setOpen(next);
  }

  return (
    <>
      <ViewBar>
        <span>
          Строк ведомости: {rows.length}
          {data.systems && ` · систем: ${data.system_list.filter((s) => s.code).length}`}
          {data.total_weight != null && ` · вес ${fmtQty(data.total_weight)} кг`}
        </span>
        {withShares.length > 0 && (
          <>
            <LinkButton
              type="button"
              onClick={() => setOpen(new Set(withShares.map((r) => r.position)))}
            >
              Развернуть все
            </LinkButton>
            <LinkButton type="button" onClick={() => setOpen(new Set())}>
              Свернуть все
            </LinkButton>
          </>
        )}
      </ViewBar>
      {!data.systems && !data.groups && (
        <Muted>
          В комплекте нет разметки систем — ведомость без разбивки (разбивка — в разделах ОВ, ВК,
          НВ, НК).
        </Muted>
      )}
      {!data.systems && data.groups && (
        <Muted>
          Разбивка по группам таблиц РД (заголовки элементов и разделов спецификации): ▶ у строки
          раскрывает её доли, сумма долей равна количеству строки.
        </Muted>
      )}
      <Grid>
        <thead>
          <tr>
            <th>№</th>
            <th>Обозначение</th>
            <th>Наименование</th>
            <th>Ед.</th>
            <th className="num">Кол-во</th>
            <th className="num">Вес, кг</th>
            <th>Источник</th>
            <th className="wrap">№ строк ведомости объёмов</th>
            <th>Проверка</th>
            <th>Сверка с листами РД</th>
            <th className="num">Строк РД</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={COLS}>
                <Muted>Строк нет.</Muted>
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <SummaryRow
                key={r.position}
                row={r}
                open={open.has(r.position)}
                onToggle={() => toggle(r.position)}
              />
            ))
          )}
        </tbody>
      </Grid>
    </>
  );
}

interface RowProps {
  row: PtoMaterialRow;
  open: boolean;
  onToggle: () => void;
}

/** Доля строки для вида: система или группа таблицы. */
interface ShareView {
  key: string;
  label: string;
  quantity: number | null;
  total_weight: number | null;
  rows: number;
  source_ref: string;
  source_rows?: string | null;
  missing_qty?: boolean;
  doubt?: string[];
  doubt_rows?: number;
}

/** Доли строки: системы (ОВ, ВК, НВ, НК), иначе группы таблиц. */
function shareRows(r: PtoMaterialRow): ShareView[] {
  const systems = r.systems ?? [];
  if (systems.length > 0) {
    return systems.map((s) => ({
      ...s,
      label: s.code ? `система ${s.label}` : 'система не определена',
    }));
  }
  return (r.groups ?? []).map((g) => ({
    ...g,
    label: g.key ? `группа ${g.label}` : 'без группы',
  }));
}

/** Строка ведомости и (раскрытые) доли систем или групп. */
function SummaryRow({ row: r, open, onToggle }: RowProps) {
  const shares = shareRows(r);
  const bySystems = (r.systems?.length ?? 0) > 0;
  const sum = shareSum({ quantity: r.quantity, systems: shares });
  return (
    <Fragment>
      <tr>
        <td>{r.position}</td>
        <td>{r.designation ?? '—'}</td>
        <td>
          {shares.length > 0 ? (
            <Toggle type="button" aria-expanded={open} onClick={onToggle}>
              {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              {r.name}
            </Toggle>
          ) : (
            r.name
          )}
          {shares.length > 0 && !open && (
            <Facts>
              {shares.length === 1
                ? shares[0].label
                : bySystems
                  ? `систем: ${shares.length}`
                  : `групп: ${shares.length}`}
            </Facts>
          )}
          {sum && !sum.matches && (
            <Warn
              role="img"
              aria-label={`Сумма по ${bySystems ? 'системам' : 'группам'} не равна количеству строки`}
              title={`Сумма по ${bySystems ? 'системам' : 'группам'} ${fmtQty(sum.sum)} ≠ ${fmtQty(r.quantity)}`}
            >
              {WARN_SIGN}
            </Warn>
          )}
        </td>
        <td>{r.unit ?? '—'}</td>
        <td className="num">
          {fmtQty(r.quantity)}
          {r.coating && (
            <QtyNote title="Площадь покрытия с учётом слоёв (в итоги не входит)">
              покрытие: {r.coating}
            </QtyNote>
          )}
        </td>
        <td className="num">{fmtQty(r.total_weight)}</td>
        <td>{r.source_ref}</td>
        <td>{r.source_rows ?? ''}</td>
        <td>{r.needs_review ? 'проверить' : ''}</td>
        <td>{r.check ?? ''}</td>
        <td className="num">{r.position_ids?.length ?? '—'}</td>
      </tr>
      {open &&
        shares.map((s) => (
          <ShareTr key={s.key}>
            <td />
            <td />
            <td className="label">
              {s.label}
              {(s.doubt?.length ?? 0) > 0 && (
                <Warn
                  role="img"
                  aria-label={`Сомнение: ${s.doubt?.join('; ')}`}
                  title={`Сомнение в системе (${s.doubt_rows ?? 0} стр.): ${s.doubt?.join('; ')}`}
                >
                  {WARN_SIGN}
                </Warn>
              )}
            </td>
            <td>{r.unit ?? '—'}</td>
            <td className="num">{fmtQty(s.quantity)}</td>
            <td className="num">{fmtQty(s.total_weight)}</td>
            <td>{s.source_ref}</td>
            <td>{s.source_rows ?? ''}</td>
            <td>{s.missing_qty ? 'кол-во не у всех строк' : ''}</td>
            <td />
            <td className="num">{s.rows}</td>
          </ShareTr>
        ))}
      {open && sum && (
        <ShareTr>
          <td />
          <td />
          <td className="label">{bySystems ? 'Σ по системам' : 'Σ по группам'}</td>
          <td />
          <td className="num">{fmtQty(Math.round(sum.sum * 1000) / 1000)}</td>
          <td />
          <td colSpan={5}>
            {sum.matches ? '= количество строки' : '≠ количество строки — проверьте'}
          </td>
        </ShareTr>
      )}
    </Fragment>
  );
}
